interface HasToJSONMethod {toJSON(): string}

// Type guard to check if an object has a `toJSON()` method
const hasOwnToJSONMethod = (input: unknown): input is HasToJSONMethod => {
  // Only objects (include dates) can have methods
  if (typeof input !== 'object' || input === null) return false
  return ((typeof (input as HasToJSONMethod).toJSON) === 'function')
}

// Helper function, used as argument in JSON.stringify, to extract
// properties from a plain Error object.
// https://stackoverflow.com/questions/18391212/is-it-not-possible-to-stringify-an-error-using-json-stringify
function replaceErrors(key: string, value: object) {
  if (value instanceof Error) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const error = {} as {[key: string]: any}
    Object.getOwnPropertyNames(value).forEach((propName) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      error[propName] = (value as {[key: string]: any})[propName]
    })
    return error
  }
  return value
}

/**
 * Returns a 'pretty printed' JSON string from the input value.
 *
 * It behaves exactly like: `JSON.stringify(input, null, 2)` except
 * that it is also able to JSON.stringify js `Error` objects
 * (JSON.stringify would just return an empty object `{}`). Also,
 * if the error has a toJSON() method (some custom build errors
 * might), it will use that method to get the JSON.
 *
 * @param input
 */
export const toPrettyJson = (input: unknown): string => {
  // for non-Errors' just use native stringify as is
  if (!(input instanceof Error)) return JSON.stringify(input, null, 2)

  // Now, we are sure to have an instance of Error
  // Convert it to Json:
  let json: string
  // if it has it's own toJSON method (some custom errors might have)
  if (hasOwnToJSONMethod(input)) {
    json = input.toJSON()
  } else {
    json = JSON.stringify(input, replaceErrors)
  }
  // parse (= to object) and then reformat to pretty json
  return JSON.stringify(JSON.parse(json), null, 2)
}
